import { ASMTreeNode } from '@/state'
import { CollapsibleTreeNodeButton } from './CollapsibleTreeNodeButton'
import { HidePartButton } from './HidePartButton'
import { AttachedToOperationIndicator } from './AttachedToOperationIndicator'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { TreeNodeCheckbox } from '@/pages/CADPage/components/AssemblyTree/TreeNodeCheckbox'
import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { RawAssemblyTree } from '@/state'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useCallback, useMemo } from 'react'
import { findAttachedStep } from './lib/findAttachedStep'
import { useStepOrderNumber } from '../AssemblyOrderPanel/hooks/useStepOrderNumber'
import { EditableField } from '@/components/core/EditableField'
import { useCADQuery } from '@/services/queries/cads'
import { useUpdateCadVersionMutation } from '../../queries'

export const TreeNodeItem = ({
  tree,
  node,
  gltf,
  isCollapsible,
  isAttachedToOperationStep,
  level,
  showHidePartButton,
  isReadOnly,
  isEditing,
  setIsEditing,
}: {
  tree: RawAssemblyTree
  node: ASMTreeNode
  gltf: GLTFObject
  isCollapsible: boolean
  isAttachedToOperationStep: boolean
  level: number
  showHidePartButton: boolean
  isReadOnly: boolean
  isEditing?: boolean
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { steps } = useOperationSteps()

  const attachedStep = useMemo(
    () => findAttachedStep({ steps, tree, node }),
    [steps, tree, node],
  )

  const {
    data: { orderNumber },
  } = useStepOrderNumber({ step: attachedStep?.step })

  const { data: cadData, refetchCadVersion } = useCADQuery()
  const { mutate: updateCadVersion } = useUpdateCadVersionMutation()

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )
  const documentVersionId = useMemo(
    () => cadData?.documentVersion?.id as string,
    [cadData?.documentVersion],
  )

  const handlePartRename = useCallback(
    async (partName: string) => {
      if (!tree) return
      const assemblyTree = { ...tree }
      const partNode = assemblyTree.nodes.find((n) => n.uuid === node.uuid)
      if (!partNode) return
      partNode.display_name = partName

      if (assemblyTree && cadVersionId && documentVersionId) {
        await updateCadVersion({
          cadVersionId,
          documentVersionId,
          assemblyTree: tree,
        })
        refetchCadVersion()
      }
    },
    [
      tree,
      node,
      cadVersionId,
      documentVersionId,
      updateCadVersion,
      refetchCadVersion,
    ],
  )

  return (
    <TooltipProvider>
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <div
            data-testid={`tree-node-${node.instance}`}
            id={`tree-node-${node.uuid}`}
            className="flex items-center py-1"
            style={{ paddingLeft: (level - 1) * 10 }}
          >
            <CollapsibleTreeNodeButton node={node} enabled={isCollapsible} />

            <div className="flex grow items-center min-w-0 pl-1 pr-4">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <TreeNodeCheckbox
                  tree={tree}
                  node={node}
                  isVisible={showHidePartButton}
                />

                <div className="flex-1 min-w-0">
                  <div className="block w-full truncate select-none">
                    {isReadOnly ? (
                      node.display_name
                    ) : (
                      <EditableField
                        value={node.display_name}
                        onSaveHandler={handlePartRename}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="ml-2 flex items-center gap-2 shrink-0">
                <HidePartButton
                  node={node}
                  isVisible={showHidePartButton}
                  gltf={gltf}
                />
                <AttachedToOperationIndicator
                  node={node}
                  isVisible={!isAttachedToOperationStep}
                  tree={tree}
                />
              </div>
            </div>
          </div>
        </TooltipTrigger>

        {isAttachedToOperationStep && (
          <TooltipContent className="bg-black text-white">
            {attachedStep && attachedStep.node.uuid === node.uuid && (
              <span>Attached to Operation {orderNumber}</span>
            )}
            {attachedStep && attachedStep.node.uuid !== node.uuid && (
              <span>
                Attached to Operation {orderNumber} through{' '}
                {attachedStep.node.display_name}
              </span>
            )}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  )
}
